// src/components/AdminComponents/sections/contractdetailsview.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Tabs, Tab, IconButton, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel,
  LinearProgress, Badge, List, ListItem, ListItemText, ListItemIcon,
  Divider, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Send as SendIcon, Visibility as ViewIcon, Assignment as AssignIcon,
  CheckCircle as SignedIcon, Pending as PendingIcon, Cancel as DeclinedIcon,
  NotificationsActive as RemindIcon, ExpandMore as ExpandMoreIcon,
  Business as CompanyIcon, Group as TeamIcon, Person as UserIcon,
  Analytics as AnalyticsIcon, History as HistoryIcon,
  Description as ContractIcon, Dashboard as DashboardIcon
} from '@mui/icons-material';
import client from "../../../../API";
import EnhancedContractTemplateDialog from './contractbuilder';
import LinkIcon from '@mui/icons-material/Link';

const ContractDetailsView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [createTemplateDialogOpen, setCreateTemplateDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchOverviewData();
    fetchTemplates();
  }, []);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const stats = await client.contracts.getSystemStats();
      setSystemStats(stats.stats);
    } catch (error) {
      console.error('Failed to fetch contract overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await client.contracts.getTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  const handleCreateTemplate = () => {
    setEditTemplate(null);
    setCreateTemplateDialogOpen(true);
  };
  const handleAssignContract = () => setAssignDialogOpen(true);
  const handleViewAnalytics = () => setActiveTab(3);
  const handleEditTemplate = (template) => {
    setEditTemplate(template);
    setCreateTemplateDialogOpen(true);
  };
  const handleCloseTemplateDialog = () => {
    setCreateTemplateDialogOpen(false);
    setEditTemplate(null);
  };
  const handleSaveTemplate = () => {
    fetchTemplates();
    fetchOverviewData();
  };
  const handleCloseAssignDialog = () => setAssignDialogOpen(false);
  const handleAssignSuccess = () => fetchOverviewData();

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon /> },
    { label: 'Templates', icon: <ContractIcon /> },
    { label: 'Assignments', icon: <SendIcon /> },
    { label: 'Analytics', icon: <AnalyticsIcon /> },
    { label: 'Pending Signatures', icon: <PendingIcon /> }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Contract Management</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage contract templates, assignments, and monitor signature progress
      </Typography>

      {systemStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Total Templates</Typography>
              <Typography variant="h4">{systemStats.total_templates}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Active Templates</Typography>
              <Typography variant="h4" color="success.main">{systemStats.active_templates}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Total Assignments</Typography>
              <Typography variant="h4">{systemStats.total_assignments}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card><CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Completion Rate</Typography>
              <Typography variant="h4" color="primary.main">
                {systemStats.completion_rate?.toFixed(1)}%
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabs.map((tab, i) => (
            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <ContractOverview 
          onCreateTemplate={handleCreateTemplate}
          onAssignContract={handleAssignContract}
          onViewAnalytics={handleViewAnalytics}
        />
      )}
      {activeTab === 1 && (
        <ContractTemplatesAdmin 
          onCreateTemplate={handleCreateTemplate}
          onEditTemplate={handleEditTemplate}
          templates={templates}
          onTemplatesChange={fetchTemplates}
        />
      )}
      {activeTab === 2 && (
        <ContractAssignmentsAdmin 
          onAssignContract={handleAssignContract}
          templates={templates}
        />
      )}
      {activeTab === 3 && <ContractAnalyticsAdmin />}
      {activeTab === 4 && <PendingSignaturesAdmin />}

      <EnhancedContractTemplateDialog
        open={createTemplateDialogOpen}
        template={editTemplate}
        onClose={handleCloseTemplateDialog}
        onSave={handleSaveTemplate}
      />
      
      <ContractAssignmentDialog
        open={assignDialogOpen}
        templates={templates}
        onClose={handleCloseAssignDialog}
        onAssign={handleAssignSuccess}
      />
    </Box>
  );
};

const ContractOverview = ({ onCreateTemplate, onAssignContract, onViewAnalytics }) => {
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOverviewData(); }, []);
  const fetchOverviewData = async () => {
    try {
      const auditLogs = await client.contracts.getAuditLogs({ page: 1, per_page: 10 });
      setRecentActivity(auditLogs.audit_logs || []);
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Recent Contract Activity</Typography>
          {recentActivity.length > 0 ? (
            <List>
              {recentActivity.slice(0, 10).map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemIcon>
                    {activity.action.includes('signed') ? <SignedIcon color="success" /> :
                     activity.action.includes('declined') ? <DeclinedIcon color="error" /> :
                     activity.action.includes('assigned') ? <SendIcon color="primary" /> :
                     <ContractIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    secondary={new Date(activity.created_at).toLocaleString()}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box textAlign="center" py={4}>
              <ContractIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">No recent activity</Typography>
              <Typography variant="body2" color="text.secondary">Create your first template to get started</Typography>
            </Box>
          )}
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Quick Actions</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} fullWidth onClick={onCreateTemplate}>
              Create Template
            </Button>
            <Button variant="outlined" startIcon={<SendIcon />} fullWidth onClick={onAssignContract}>
              Assign Contract
            </Button>
            <Button variant="outlined" startIcon={<AnalyticsIcon />} fullWidth onClick={onViewAnalytics}>
              View Analytics
            </Button>
          </Box>
        </CardContent></Card>
        <Card sx={{ mt: 2 }}><CardContent>
          <Typography variant="h6" gutterBottom>Today's Summary</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><PendingIcon color="warning" /></ListItemIcon>
              <ListItemText primary="Pending Signatures" secondary="Check assignments needing attention" />
            </ListItem>
            <ListItem>
              <ListItemIcon><SignedIcon color="success" /></ListItemIcon>
              <ListItemText primary="Recent Completions" secondary="View latest signed contracts" />
            </ListItem>
          </List>
        </CardContent></Card>
      </Grid>
    </Grid>
  );
};

const ContractTemplatesAdmin = ({ onCreateTemplate, onEditTemplate, templates, onTemplatesChange }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (templates.length === 0) fetchTemplates(); else setLoading(false);
  }, [templates]);

  const fetchTemplates = async () => {
    try {
      await client.contracts.getTemplates();
      onTemplatesChange && onTemplatesChange();
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await client.contracts.deleteTemplate(id);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template. It may have active assignments.');
    }
  };

  const toggleTemplateStatus = async (template) => {
    try {
      const newStatus = template.status === 'active' ? 'archived' : 'active';
      await client.contracts.updateTemplate(template.id, { status: newStatus });
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to update template status:', error);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Contract Templates ({templates.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>Create Template</Button>
      </Box>
      {templates.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Status</TableCell><TableCell>Version</TableCell>
              <TableCell>Assignments</TableCell><TableCell>Created</TableCell><TableCell>Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.status} color={t.status === 'active' ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>v{t.version}</TableCell>
                  <TableCell>
                    <Badge badgeContent={t.assignment_count} color="primary"><AssignIcon /></Badge>
                  </TableCell>
                  <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditTemplate(t)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={t.status === 'active' ? 'Archive' : 'Activate'}>
                        <IconButton size="small" onClick={() => toggleTemplateStatus(t)}>
                          {t.status === 'active' ? '📦' : '✅'}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTemplate(t.id)}
                          disabled={t.assignment_count > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card><CardContent>
          <Box textAlign="center" py={8}>
            <ContractIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No Templates Yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first contract template to get started with digital signatures
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateTemplate}>Create Your First Template</Button>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

const ContractAssignmentsAdmin = ({ onAssignContract, templates }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const BLITZ_ROOT = 'https://www.useblitz.co';
  const buildContractLink = (assignmentId) => `${BLITZ_ROOT}/contract/${assignmentId}`;
  const handleOpenPublicLink = (assignmentId) => window.open(buildContractLink(assignmentId), '_blank', 'noopener');

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const assignmentsData = await client.contracts.getAssignments();
      setAssignments(assignmentsData.assignments || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async (assignmentId) => {
    try {
      const result = await client.contracts.sendReminders(assignmentId);
      alert(`Sent ${result.reminder_count} reminders successfully`);
      fetchData();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Contract Assignments ({assignments.length})</Typography>
        <Button
          variant="contained"
          startIcon={<AssignIcon />}
          onClick={onAssignContract}
          disabled={templates.filter(t => t.status === 'active').length === 0}
        >
          Assign Contract
        </Button>
      </Box>

      {assignments.length > 0 ? (
        assignments.map(a => (
          <Accordion key={a.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%">
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="medium">{a.template?.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.assignment_scope} • {a.signature_stats?.total} signatures
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    icon={a.assignment_scope === 'company' ? <CompanyIcon /> :
                          a.assignment_scope === 'team' ? <TeamIcon /> : <UserIcon />}
                    label={a.assignment_scope}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${a.signature_stats?.signed}/${a.signature_stats?.total}`}
                    color={a.signature_stats?.completion_rate === 100 ? 'success' : 'info'}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Message:</strong> {a.assignment_message || 'No message'}
                  </Typography>
                  {a.due_date && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Due Date:</strong> {new Date(a.due_date).toLocaleDateString()}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Completion Progress</Typography>
                      <Typography variant="body2">{a.signature_stats?.completion_rate?.toFixed(1)}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={a.signature_stats?.completion_rate || 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip icon={<SignedIcon />} label={`${a.signature_stats?.signed} Signed`} color="success" size="small" />
                    <Chip icon={<PendingIcon />} label={`${a.signature_stats?.pending} Pending`} color="warning" size="small" />
                    <Chip icon={<DeclinedIcon />} label={`${a.signature_stats?.declined} Declined`} color="error" size="small" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<RemindIcon />}
                      onClick={() => handleSendReminders(a.id)}
                      disabled={a.signature_stats?.pending === 0}
                      size="small"
                    >
                      Send Reminders ({a.signature_stats?.pending})
                    </Button>
                    <Button variant="outlined" startIcon={<ViewIcon />} size="small">
                      View Details
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenPublicLink(a.id);
                      }}
                    >
                      Public Link
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Card><CardContent>
          <Box textAlign="center" py={8}>
            <AssignIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No Contract Assignments</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {templates.filter(t => t.status === 'active').length === 0 
                ? 'Create some templates first, then assign them to users, teams, or companies'
                : 'Assign contracts to users, teams, or entire companies to start collecting signatures'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AssignIcon />}
              onClick={onAssignContract}
              disabled={templates.filter(t => t.status === 'active').length === 0}
            >
              {templates.filter(t => t.status === 'active').length === 0 ? 'Create Templates First' : 'Create Assignment'}
            </Button>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

const ContractAssignmentDialog = ({ open, templates, onClose, onAssign }) => {
  const [formData, setFormData] = useState({
    template_id: '',
    scope: 'company',
    company_id: '',
    team_id: '',
    user_id: '',
    due_date: '',
    message: '',
    is_mandatory: true,
    auto_remind: true,
    reminder_frequency_days: 7
  });

  const [companies, setCompanies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState({
    companies: false, teams: false, users: false, submitting: false
  });

  useEffect(() => { if (open) fetchOptions(); }, [open]);
  useEffect(() => {
    if (formData.scope === 'team' && formData.company_id) fetchTeams(formData.company_id);
    if (formData.scope === 'user' && formData.company_id) fetchUsers(formData.company_id);
  }, [formData.scope, formData.company_id]);

  const fetchOptions = async () => {
    try {
      setLoading(p => ({ ...p, companies: true }));
      const companiesData = await client.companies.list();
      const companiesArray = Array.isArray(companiesData)
        ? companiesData
        : companiesData?.companies || [];
      setCompanies(companiesArray);
    } catch (error) { console.error('Failed to fetch companies:', error); }
    finally { setLoading(p => ({ ...p, companies: false })); }
  };

  const fetchTeams = async (companyId) => {
    try {
      setLoading(p => ({ ...p, teams: true }));
      const teamsData = await client.companies.teams.list(companyId);
      const teamsArray = Array.isArray(teamsData)
        ? teamsData
        : teamsData?.teams || [];
      setTeams(teamsArray);
    } catch (error) {
      console.error('Failed to fetch teams:', error); setTeams([]);
    } finally { setLoading(p => ({ ...p, teams: false })); }
  };

  const fetchUsers = async (companyId) => {
    try {
      setLoading(p => ({ ...p, users: true }));
      const res = await client.companies.getUsers(companyId);
      const usersArray = Array.isArray(res) ? res : res.users || [];
      setUsers(usersArray);
    } catch (err) {
      console.error('Failed to fetch users:', err); setUsers([]);
    } finally { setLoading(p => ({ ...p, users: false })); }
  };

  const handleScopeChange = (scope) => {
    setFormData(prev => ({ ...prev, scope, team_id: '', user_id: '' }));
    if (scope !== 'team') setTeams([]);
    if (scope !== 'user') setUsers([]);
  };
  const handleCompanyChange = (company_id) => {
    setFormData(prev => ({ ...prev, company_id, team_id: '', user_id: '' }));
    setTeams([]); setUsers([]);
  };

  const handleSubmit = async () => {
    if (!formData.template_id || !formData.scope || !formData.company_id) {
      alert('Template, scope, and company are required');
      return;
    }
    if (formData.scope === 'team' && !formData.team_id) return alert('Team selection is required');
    if (formData.scope === 'user' && !formData.user_id) return alert('User selection is required');

    try {
      setLoading(p => ({ ...p, submitting: true }));
      await client.contracts.assignContract(formData);
      onAssign(); onClose();
      setFormData({
        template_id: '', scope: 'company', company_id: '', team_id: '', user_id: '',
        due_date: '', message: '', is_mandatory: true, auto_remind: true, reminder_frequency_days: 7
      });
    } catch (error) {
      console.error('Failed to assign contract:', error);
      alert('Failed to assign contract');
    } finally {
      setLoading(p => ({ ...p, submitting: false }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Contract</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Contract Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData(p => ({ ...p, template_id: e.target.value }))}
                disabled={loading.submitting}
              >
                {templates.filter(t => t.status === 'active').map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Assignment Scope</InputLabel>
              <Select
                value={formData.scope}
                onChange={(e) => handleScopeChange(e.target.value)}
                disabled={loading.submitting}
              >
                <MenuItem value="company">Entire Company</MenuItem>
                <MenuItem value="team">Specific Team</MenuItem>
                <MenuItem value="user">Individual User</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={formData.company_id}
                onChange={(e) => handleCompanyChange(e.target.value)}
                disabled={loading.companies || loading.submitting}
              >
                {loading.companies
                  ? <MenuItem disabled>Loading companies...</MenuItem>
                  : companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {formData.scope === 'team' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select
                  value={formData.team_id}
                  onChange={(e) => setFormData(p => ({ ...p, team_id: e.target.value }))}
                  disabled={!formData.company_id || loading.teams || loading.submitting}
                >
                  {loading.teams
                    ? <MenuItem disabled>Loading teams...</MenuItem>
                    : teams.length === 0 && formData.company_id
                      ? <MenuItem disabled>No teams found</MenuItem>
                      : teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          )}
          {formData.scope === 'user' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={formData.user_id}
                  onChange={(e) => setFormData(p => ({ ...p, user_id: e.target.value }))}
                  disabled={!formData.company_id || loading.users || loading.submitting}
                >
                  {loading.users
                    ? <MenuItem disabled>Loading users...</MenuItem>
                    : users.length === 0 && formData.company_id
                      ? <MenuItem disabled>No users found</MenuItem>
                      : users.map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </MenuItem>
                      ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData(p => ({ ...p, due_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={loading.submitting}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assignment Message"
              multiline
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
              helperText="Optional message to include with the contract assignment"
              disabled={loading.submitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading.submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading.submitting}>
          {loading.submitting ? <CircularProgress size={20} /> : 'Assign Contract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContractAnalyticsAdmin = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { fetchAnalytics(); }, []);
  const fetchAnalytics = async () => {
    try {
      const systemStats = await client.contracts.getSystemStats();
      setAnalytics(systemStats.stats);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally { setLoading(false); }
  };
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>System-wide Statistics</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Total Templates</Typography><Typography variant="h4">{analytics?.total_templates}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Active Templates</Typography><Typography variant="h4" color="success.main">{analytics?.active_templates}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Total Assignments</Typography><Typography variant="h4">{analytics?.total_assignments}</Typography></Grid>
            <Grid item xs={6}><Typography variant="body2" color="text.secondary">Overall Completion Rate</Typography><Typography variant="h4" color="primary.main">{analytics?.completion_rate?.toFixed(1)}%</Typography></Grid>
          </Grid>
        </CardContent></Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Signature Status Breakdown</Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <SignedIcon color="success" fontSize="large" />
                <Typography variant="h5" color="success.main">{analytics?.signed_contracts}</Typography>
                <Typography variant="body2" color="text.secondary">Signed</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <PendingIcon color="warning" fontSize="large" />
                <Typography variant="h5" color="warning.main">{analytics?.pending_contracts}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <DeclinedIcon color="error" fontSize="large" />
                <Typography variant="h5" color="error.main">
                  {analytics?.total_signatures - analytics?.signed_contracts - analytics?.pending_contracts}
                </Typography>
                <Typography variant="body2" color="text.secondary">Declined/Expired</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent></Card>
      </Grid>
    </Grid>
  );
};

const PendingSignaturesAdmin = () => {
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => { fetchPendingSignatures(); }, []);
  const fetchPendingSignatures = async () => {
    try {
      const assignments = await client.contracts.getAssignments();
      const pendingAssignments = assignments.assignments?.filter(
        a => a.signature_stats?.pending > 0
      ) || [];
      setPendingSignatures(pendingAssignments);
    } catch (error) {
      console.error('Failed to fetch pending signatures:', error);
    } finally { setLoading(false); }
  };

  // NEW: reminder handler (same logic as assignments)
  const handleSendReminders = async (assignmentId) => {
    try {
      setSendingId(assignmentId);
      const result = await client.contracts.sendReminders(assignmentId);
      alert(`Sent ${result.reminder_count} reminders successfully`);
      fetchPendingSignatures();
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders');
    } finally {
      setSendingId(null);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignments with Pending Signatures ({pendingSignatures.length})
      </Typography>
      {pendingSignatures.map(a => (
        <Card key={a.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="start">
              <Box>
                <Typography variant="h6">{a.template?.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {a.assignment_scope} assignment
                </Typography>
                <Typography variant="body2">
                  {a.signature_stats?.pending} pending signatures out of {a.signature_stats?.total}
                </Typography>
                {a.due_date && (
                  <Typography variant="body2" color="warning.main">
                    Due: {new Date(a.due_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap={1} alignItems="end">
                <Chip
                  label={`${a.signature_stats?.pending} pending`}
                  color="warning"
                  size="small"
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RemindIcon />}
                  disabled={sendingId === a.id}
                  onClick={() => handleSendReminders(a.id)}
                >
                  {sendingId === a.id ? 'Sending...' : 'Send Reminders'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={a.signature_stats?.completion_rate || 0}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {a.signature_stats?.completion_rate?.toFixed(1)}% complete
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ))}

      {pendingSignatures.length === 0 && (
        <Card><CardContent>
          <Box textAlign="center" py={4}>
            <SignedIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>All contracts are up to date!</Typography>
            <Typography variant="body2" color="text.secondary">
              No pending signatures require attention.
            </Typography>
          </Box>
        </CardContent></Card>
      )}
    </Box>
  );
};

export default ContractDetailsView;
